import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Hotel } from "../../types/hotel.d";
import { Role, UpdateUserDTO } from "../../types/user.d";
import UserService from "../../api/userService";
import { useAuth } from "../../context/authContext";

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UpdateUserDTO;
  hotels: Hotel[];
  selectedHotel: Hotel | null;
  refreshUsers: () => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  user,
  hotels,
  selectedHotel,
  refreshUsers,
}) => {
  const { user: currentUser } = useAuth();

  const [editData, setEditData] = useState<UpdateUserDTO>({
    keycloakId: user.keycloakId,
    username: user.username,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    fk_Hotel_ID: user.fk_Hotel_ID,
    role: user.role || "User",
    password: "",
  });

  const [selectedHotelForEdit, setSelectedHotelForEdit] =
    useState<Hotel | null>(selectedHotel);
  const [roles, setRoles] = useState<Role[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string | null }>({});
  const [loading, setLoading] = useState(false);
  const isTargetUserAdmin = user.role === "Admin";

  useEffect(() => {
    const currentHotel =
      hotels.find((hotel) => hotel.id === user.fk_Hotel_ID) || selectedHotel;
    setSelectedHotelForEdit(currentHotel);
    setEditData({
      username: user.username,
      keycloakId: user.keycloakId,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      fk_Hotel_ID: currentHotel ? currentHotel.id : 0,
      role: user.role || "User",
      password: "",
    });
    setErrors({});
  }, [user, hotels, selectedHotel]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const data = await UserService.getRoles();
        setRoles(data);
      } catch (error) {
        console.error("Erreur lors du chargement des rôles :", error);
      }
    };
    fetchRoles();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
  };

  const handleHotelChange = (event: any, value: Hotel | null) => {
    setSelectedHotelForEdit(value);
    setEditData((prev) => ({
      ...prev,
      fk_Hotel_ID: value ? value.id : 0,
    }));
    setErrors((prevErrors) => ({ ...prevErrors, hotelId: null }));
  };

  const validateFields = () => {
    const newErrors: { [key: string]: string | null } = {};

    if (!editData.username)
      newErrors.username = "Veuillez entrer un nom d'utilisateur";

    if (
      !editData.email ||
      !/\S+@\S+\.\S+/.test(editData.email) ||
      editData.email.length > 100
    ) {
      newErrors.email = "Veuillez entrer un email valide (max 100 caractères).";
    }

    if (
      editData.password &&
      (editData.password.length < 8 ||
        !/(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>])/.test(editData.password))
    ) {
      newErrors.password =
        "Le mot de passe doit contenir au moins 8 caractères, un chiffre et un caractère spécial.";
    }

    if (
      !editData.firstName ||
      editData.firstName.length > 50 ||
      !/^[A-Za-zÀ-ÿ]+(-[A-Za-zÀ-ÿ]+)*$/.test(editData.firstName)
    ) {
      newErrors.firstName =
        "Le prénom est requis, doit contenir uniquement des lettres ou des tirets simples (max 50 caractères).";
    }

    if (
      !editData.lastName ||
      editData.lastName.length > 50 ||
      !/^[A-Za-zÀ-ÿ]+(-[A-Za-zÀ-ÿ]+)*$/.test(editData.lastName)
    ) {
      newErrors.lastName =
        "Le nom de famille est requis, doit contenir uniquement des lettres ou des tirets simples (max 50 caractères).";
    }

    if (!editData.role) {
      newErrors.role = "Le rôle est requis.";
    }

    if (selectedHotelForEdit && selectedHotelForEdit.id <= 0) {
      newErrors.hotelId = "L'ID de l'hôtel doit être un entier positif.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateFields()) return;

    const hasChanges =
      editData.username !== user.username ||
      editData.email !== user.email ||
      editData.password?.trim() !== "" ||
      editData.firstName !== user.firstName ||
      editData.lastName !== user.lastName ||
      editData.role !== user.role ||
      selectedHotelForEdit?.id !== user.fk_Hotel_ID;

    if (hasChanges) {
      const updateData: UpdateUserDTO = {
        ...editData,
        fk_Hotel_ID: selectedHotelForEdit ? selectedHotelForEdit.id : 0,
      };
      if (!editData.password?.trim()) {
        delete updateData.password;
      }
      if(editData.username === user.username) {
        delete updateData.username; // Le nom d'utilisateur n'a pas changé => on ne dois pas l'envoyer, sinon le backend considère qu'il existe déjà. 
      }
      setLoading(true);
      try {
        await UserService.updateUser(updateData);
        await refreshUsers();
        onClose();
      } catch (error: any) {
        console.error("Erreur lors de la mise à jour de l'utilisateur:", error);
        setErrors({
          // À revoir éventuellement, mais pour l'instant c'est simple et ça fonctionne
          general: error == "Username already exists" ? "Ce nom d'utilisateur existe déjà" : "L'ajout de l'utilisateur a échoué. Veuillez réessayer.",
        });
      } finally {
        setLoading(false);
      }
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Modifier l'utilisateur</DialogTitle>
      <DialogContent>
        <TextField
          label="Nom d'utilisateur"
          name="username"
          value={editData.username || ""}
          onChange={handleInputChange}
          fullWidth
          margin="dense"
          required
          error={!!errors.username}
          helperText={errors.username}
        />
        <TextField
          label="Email"
          name="email"
          value={editData.email || ""}
          onChange={handleInputChange}
          fullWidth
          margin="dense"
          required
          error={!!errors.email}
          helperText={errors.email}
        />
        <TextField
          label="Mot de passe"
          name="password"
          type="password"
          value={editData.password || ""}
          onChange={handleInputChange}
          fullWidth
          margin="dense"
          helperText={
            errors.password ||
            "Laissez vide pour conserver le mot de passe actuel"
          }
          error={!!errors.password}
        />
        <TextField
          label="Prénom"
          name="firstName"
          value={editData.firstName || ""}
          onChange={handleInputChange}
          fullWidth
          margin="dense"
          error={!!errors.firstName}
          helperText={errors.firstName}
        />
        <TextField
          label="Nom de famille"
          name="lastName"
          value={editData.lastName || ""}
          onChange={handleInputChange}
          fullWidth
          margin="dense"
          error={!!errors.lastName}
          helperText={errors.lastName}
        />

        <Autocomplete
          options={roles.filter(
            (role) => (currentUser?.role === "Admin" || role.name !== "Admin") && role.name !== "Manager" // Rôle inutile, à retirer du backend éventuellement
          )}
          getOptionLabel={(option) => option.name}
          value={roles.find((role) => role.name === editData.role) || null}
          onChange={(event, value) => {
            if (!isTargetUserAdmin) {
              setEditData((prev) => ({
                ...prev,
                role: value?.name || "",
              }));
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Rôle"
              margin="dense"
              required
              disabled={isTargetUserAdmin}
              error={!isTargetUserAdmin && !!errors.role}
              helperText={
                isTargetUserAdmin
                  ? "Vous ne possédez pas les droits suffisants pour modifier ce rôle."
                  : errors.role
              }
            />
          )}
          fullWidth
          sx={{ mt: 2 }}
          disabled={isTargetUserAdmin}
        />

        <Autocomplete
          options={hotels}
          getOptionLabel={(option) => option.name}
          value={selectedHotelForEdit}
          onChange={handleHotelChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Hôtel associé"
              error={!!errors.hotelId}
              helperText={errors.hotelId}
              required
            />
          )}
          fullWidth
          sx={{ mt: 2 }}
        />

        {errors.general && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {errors.general}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {loading ? "Sauvegarde en cours..." : "Sauvegarder"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserModal;
